import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

import type { ChoiceContent, PictureContent, TrueFalseAnswer, TrueFalseContent } from '../contentTypes'
import type { RendererProps } from './GapRenderers'
import { VerdictMark } from './VerdictMark'
import { VERDICT_BORDER_WIDTH, verdictAccent, verdictTint, type VerdictOutcome } from './verdictStyles'

/**
 * Рендереры типов с выбором. Ответы: `{ selected: [0, 2] }` для вариантов,
 * `{ statements: ["true", …] }` для верно/неверно, `{ hotspots: […] }` или
 * `{ selected: 1 }` для картинок.
 */

/**
 * Тон подсветки разбора: ответ ученика верный, ответ ученика неверный и
 * пропущенный верный вариант — тот, который ученик выбрать был должен, но не
 * выбрал.
 */
type CheckTone = 'ok' | 'wrong' | 'missed'

/**
 * Разбор, пригодный для показа.
 *
 * Красим только закрытую работу: решающему ученику `check` не приходит вовсе, а
 * пустой объект значит «сервер разбор не считал» — на нём подсветки тоже нет, и
 * задание выглядит ровно как до проверки.
 */
function reviewCheck(
  check: Record<string, unknown> | null | undefined,
  readOnly: boolean | undefined,
): Record<string, unknown> | null {
  if (readOnly !== true || check === null || check === undefined) return null

  return Object.keys(check).length > 0 ? check : null
}

/**
 * Карта результатов по элементам.
 *
 * Ключи в ней числовые, и сервер отдаёт такую карту то объектом `{"0": …}`, то
 * массивом — после JSON-раунда числовые ключи схлопываются в список. Незнакомая
 * форма даёт пустую карту: подсветки не будет, но и падения тоже.
 */
function checkMap(value: unknown): Record<string, unknown> {
  if (Array.isArray(value)) return Object.fromEntries(value.map((item, index) => [String(index), item]))
  if (typeof value === 'object' && value !== null) return value as Record<string, unknown>

  return {}
}

/**
 * Исход тона для общих хелперов вердикта (`verdictStyles`). Пропущенный верный
 * вариант — верный: его отличают пунктир и отсутствие подложки, а не цвет.
 */
function toneOutcome(tone: CheckTone): VerdictOutcome {
  return tone === 'wrong' ? 'wrong' : 'correct'
}

/**
 * Рамка и подложка проверенного варианта; текст — основным цветом (EA-596):
 * зелёный текст на зелёной подложке читался на 2.2:1.
 *
 * Пропущенный верный вариант рисуем пунктирной рамкой без заливки: ученик его
 * не выбирал, и залить его как ответ значило бы приписать ему чужой выбор.
 * Цвета дублируем для `.Mui-disabled` — на проверенной работе контролы
 * выключены, и MUI красит их серым поверх нашей подсветки.
 */
function optionSx(tone: CheckTone): SxProps<Theme> {
  return (theme: Theme) => {
    const outcome = toneOutcome(tone)
    const accent = verdictAccent(theme, outcome)
    const text = theme.palette.text.primary

    return {
      borderRadius: 1,
      py: 0.25,
      pr: 1,
      borderWidth: VERDICT_BORDER_WIDTH,
      borderStyle: tone === 'missed' ? 'dashed' : 'solid',
      borderColor: accent,
      backgroundColor: tone === 'missed' ? 'transparent' : verdictTint(theme, outcome),
      '& .MuiFormControlLabel-label, & .MuiFormControlLabel-label.Mui-disabled': { color: text },
      '& .MuiCheckbox-root.Mui-disabled, & .MuiRadio-root.Mui-disabled': { color: accent },
    }
  }
}

/** Подпись со значком вердикта: при дальтонизме рамки одного цвета */
function labelWithMark(text: string, tone: CheckTone | null) {
  if (tone === null) return text

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {text}
      <VerdictMark outcome={toneOutcome(tone)} />
    </Box>
  )
}

/**
 * Индексы верных вариантов из разбора.
 *
 * Поэлементного `ok` у выбора нет: сервер отдаёт только список `correct`, и
 * «верно/неверно» по каждому варианту считаем сами — пересечением со списком
 * ученика.
 */
function correctIndexes(check: Record<string, unknown> | null): Set<number> | null {
  if (check === null) return null

  const raw = check.correct
  const list = Array.isArray(raw) ? raw : typeof raw === 'object' && raw !== null ? Object.values(raw) : null

  if (list === null) return null

  const indexes = new Set<number>()

  for (const item of list) {
    const index =
      typeof item === 'number' ? item : typeof item === 'string' && item.trim() !== '' ? Number(item) : Number.NaN

    if (Number.isInteger(index)) indexes.add(index)
  }

  // Пустой список — не «всё неверно», а отсутствие данных: покрасить весь
  // вопрос красным на пустом разборе значит соврать ученику
  return indexes.size > 0 ? indexes : null
}

export function ChoiceRenderer({ content, answers, onChange, readOnly, check }: RendererProps<ChoiceContent>) {
  const { t } = useTranslation()
  const selected = Array.isArray(answers.selected) ? (answers.selected as number[]) : []
  const multiple = content.multiple === true
  const correct = correctIndexes(reviewCheck(check, readOnly))

  const toneHints: Record<CheckTone, string> = {
    ok: t('exercises.run.checkCorrect', 'Верно'),
    wrong: t('exercises.run.checkWrong', 'Ошибка'),
    missed: t('exercises.run.checkMissed', 'Пропущенный верный вариант'),
  }

  // Выбран и верен — зелёный, выбран и неверен — красный, не выбран, но верен —
  // мягкая рамка: учителю нужно видеть и то, что ученик пропустил
  const toneOf = (index: number): CheckTone | null => {
    if (correct === null) return null
    if (selected.includes(index)) return correct.has(index) ? 'ok' : 'wrong'

    return correct.has(index) ? 'missed' : null
  }

  // Без разбора пропсы пустые, и вариант рисуется ровно как до проверки
  const checkProps = (index: number): { sx?: SxProps<Theme>; title?: string } => {
    const tone = toneOf(index)

    return tone === null ? {} : { sx: optionSx(tone), title: toneHints[tone] }
  }

  const toggle = (index: number) => {
    if (multiple) {
      const next = selected.includes(index) ? selected.filter((item) => item !== index) : [...selected, index]
      onChange({ ...answers, selected: next })

      return
    }

    onChange({ ...answers, selected: [index] })
  }

  return (
    <Stack spacing={2}>
      <Typography>{content.question}</Typography>

      {multiple ? (
        <Stack>
          {content.options.map((option, index) => (
            <FormControlLabel
              key={index}
              {...checkProps(index)}
              control={
                <Checkbox checked={selected.includes(index)} disabled={readOnly} onChange={() => toggle(index)} />
              }
              label={labelWithMark(option.text, toneOf(index))}
            />
          ))}
        </Stack>
      ) : (
        <RadioGroup value={selected[0] ?? -1}>
          {content.options.map((option, index) => (
            <FormControlLabel
              key={index}
              value={index}
              {...checkProps(index)}
              control={<Radio disabled={readOnly} onChange={() => toggle(index)} />}
              label={labelWithMark(option.text, toneOf(index))}
            />
          ))}
        </RadioGroup>
      )}
    </Stack>
  )
}

/** Ответ верно/неверно из разбора: сервер шлёт булево, но строку тоже переживём */
function trueFalseAnswer(value: unknown): TrueFalseAnswer | null {
  if (value === true || value === 'true') return 'true'
  if (value === false || value === 'false') return 'false'
  if (value === 'not_stated') return 'not_stated'

  return null
}

/** Результат по утверждению: без булева `ok` красить нечего */
function statementCheck(
  check: Record<string, unknown> | null,
  index: number,
): { ok: boolean; correct: TrueFalseAnswer | null } | null {
  if (check === null) return null

  const item = checkMap(check.statements)[String(index)]

  if (typeof item !== 'object' || item === null) return null

  const { ok, correct } = item as { ok?: unknown; correct?: unknown }

  if (typeof ok !== 'boolean') return null

  return { ok, correct: trueFalseAnswer(correct) }
}

/**
 * Подсветка кнопки «верно/неверно»: рамка и подложка вердикта, текст основным
 * цветом — как у вариантов выбора. Пунктир — у правильного ответа, который
 * ученик не выбрал; цвета дублируем для `.Mui-disabled` по той же причине.
 */
function toggleSx(tone: CheckTone): SxProps<Theme> {
  return (theme: Theme) => {
    const outcome = toneOutcome(tone)

    return {
      // Двойной класс: у учителя общий readableStudentAnswerSx красит выбранную
      // выключенную кнопку правилом специфичнее одиночного
      '&&, &&.Mui-selected, &&.Mui-disabled, &&.Mui-selected.Mui-disabled': {
        color: theme.palette.text.primary,
        borderColor: verdictAccent(theme, outcome),
        borderStyle: tone === 'missed' ? 'dashed' : 'solid',
        backgroundColor: tone === 'missed' ? 'transparent' : verdictTint(theme, outcome),
      },
    }
  }
}

export function TrueFalseRenderer({ content, answers, onChange, readOnly, check }: RendererProps<TrueFalseContent>) {
  const { t } = useTranslation()
  const given = Array.isArray(answers.statements) ? (answers.statements as string[]) : []
  const review = reviewCheck(check, readOnly)

  const set = (index: number, value: string) => {
    const next = [...given]
    next[index] = value
    onChange({ ...answers, statements: next })
  }

  const toneHints: Record<CheckTone, string> = {
    ok: t('exercises.run.checkCorrect', 'Верно'),
    wrong: t('exercises.run.checkWrong', 'Ошибка'),
    missed: t('exercises.run.checkMissed', 'Пропущенный верный вариант'),
  }

  const answerLabels: Record<TrueFalseAnswer, string> = {
    true: t('exercises.run.true'),
    false: t('exercises.run.false'),
    not_stated: t('exercises.run.notStated'),
  }

  return (
    <Stack spacing={2}>
      {content.text && <Typography sx={{ whiteSpace: 'pre-wrap' }}>{content.text}</Typography>}

      {content.statements.map((statement, index) => {
        const result = statementCheck(review, index)
        // Правильный ответ показываем только ошибившемуся: угадавшему он и так
        // перед глазами — это его собственная кнопка
        const missed = result !== null && !result.ok ? result.correct : null

        const toneOf = (value: TrueFalseAnswer): CheckTone | null => {
          if (result === null) return null
          if (given[index] === value) return result.ok ? 'ok' : 'wrong'

          return value === missed ? 'missed' : null
        }

        // Без разбора пропсы пустые, и кнопки остаются такими же, как у решающего
        const checkProps = (value: TrueFalseAnswer): { sx?: SxProps<Theme>; title?: string } => {
          const tone = toneOf(value)

          return tone === null ? {} : { sx: toggleSx(tone), title: toneHints[tone] }
        }

        const skipped = result !== null && (given[index] ?? '') === ''

        return (
          <Stack
            key={index}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'flex-start' }}
          >
            <Typography sx={{ flex: 1, pt: { sm: 0.5 } }}>{statement.text}</Typography>
            {/* Переключатель и подпись — своей колонкой: пока подпись стояла
                третьим элементом строки, длинное утверждение сдвигало кнопки, и
                они уезжали то влево, то за край. */}
            <Stack spacing={0.5} alignItems={{ sm: 'flex-end' }} sx={{ flexShrink: 0 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={given[index] ?? null}
              disabled={readOnly}
              onChange={(_, value) => value && set(index, String(value))}
            >
              <ToggleButton value="true" {...checkProps('true')}>
                {labelWithMark(t('exercises.run.true'), toneOf('true'))}
              </ToggleButton>
              <ToggleButton value="false" {...checkProps('false')}>
                {labelWithMark(t('exercises.run.false'), toneOf('false'))}
              </ToggleButton>
              {content.allow_not_stated && (
                <ToggleButton value="not_stated" {...checkProps('not_stated')}>
                  {labelWithMark(t('exercises.run.notStated'), toneOf('not_stated'))}
                </ToggleButton>
              )}
            </ToggleButtonGroup>

              {/* Цвет читается не всеми: подпись прямым текстом называет верный
                  ответ, когда ученик ошибся. Как эталон пропуска: галочка и
                  основной цвет, слово «Верный ответ» — в title */}
              {missed !== null && (
                <Typography
                  variant="caption"
                  title={t('exercises.run.checkReference')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: 'text.primary' }}
                >
                  <VerdictMark outcome="correct" fontSize="inherit" />
                  {answerLabels[missed]}
                </Typography>
              )}

              {/* Пропущенное — серым и словом: пустая строка среди
                  раскрашенных читается как «здесь всё в порядке». */}
              {skipped && (
                <Typography variant="caption" color="text.disabled">
                  {t('exercises.run.checkSkipped')}
                </Typography>
              )}
            </Stack>
          </Stack>
        )
      })}
    </Stack>
  )
}

export function PictureRenderer({ content, answers, onChange, readOnly }: RendererProps<PictureContent>) {
  const { t } = useTranslation()

  if (content.variant === 'pick_image') {
    const selected = typeof answers.selected === 'number' ? (answers.selected as number) : null

    return (
      <Stack spacing={2}>
        {content.prompt && <Typography>{content.prompt}</Typography>}
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {(content.options ?? []).map((option, index) => (
            <Box
              key={index}
              onClick={() => !readOnly && onChange({ ...answers, selected: index })}
              sx={{
                p: 0.5,
                border: 3,
                borderRadius: 1,
                borderColor: selected === index ? 'primary.main' : 'transparent',
                cursor: readOnly ? 'default' : 'pointer',
              }}
            >
              <Box
                component="img"
                src={option.image_url ?? option.image_path}
                alt=""
                sx={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 1, display: 'block' }}
              />
            </Box>
          ))}
        </Stack>
      </Stack>
    )
  }

  const given = Array.isArray(answers.hotspots) ? (answers.hotspots as string[]) : []

  return (
    <Stack spacing={2}>
      <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
        <Box
          component="img"
          src={content.image_url ?? content.image_path ?? ''}
          alt=""
          sx={{ maxWidth: '100%', display: 'block', borderRadius: 1 }}
        />
        {(content.hotspots ?? []).map((hotspot, index) => {
          const w = hotspot.w ?? 0
          const h = hotspot.h ?? 0
          // Без размеров область рисуется точкой: так размечены задания,
          // созданные до выделения рамкой
          const isPoint = w <= 0 || h <= 0

          return (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                left: `${(hotspot.x - w / 2) * 100}%`,
                top: `${(hotspot.y - h / 2) * 100}%`,
                display: 'flex',
                ...(isPoint
                  ? {
                      transform: 'translate(-50%, -50%)',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                    }
                  : {
                      width: `${w * 100}%`,
                      height: `${h * 100}%`,
                      borderRadius: 1,
                      border: 2,
                      borderColor: 'primary.main',
                      bgcolor: 'rgba(103, 58, 183, 0.18)',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                    }),
              }}
            >
              {isPoint ? (
                index + 1
              ) : (
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.5,
                    borderRadius: '10px',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: 12,
                    lineHeight: '20px',
                    textAlign: 'center',
                  }}
                >
                  {index + 1}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>

      <Stack spacing={1}>
        {(content.hotspots ?? []).map((_, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <Typography sx={{ width: 28 }}>{index + 1}.</Typography>
            <Box
              component="input"
              value={given[index] ?? ''}
              disabled={readOnly}
              placeholder={t('exercises.run.hotspotPlaceholder')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const next = [...given]
                next[index] = event.target.value
                onChange({ ...answers, hotspots: next })
              }}
              sx={{
                flex: 1,
                p: 1,
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'transparent',
                color: 'text.primary',
                font: 'inherit',
              }}
            />
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
}
